/**
 * Pure summarizers + types for the read-only "AI Quality & Observability" admin page.
 *
 * This file intentionally has NO runtime Supabase/React import (only type-only imports,
 * which are erased) so the aggregation + honest-empty logic is unit-testable in Jest
 * without dragging in the native Supabase client. The React Query hooks that read the
 * tables live in ./use-ai-quality.ts and re-export everything here.
 *
 * Everything shown by the page is derived here from safe values only — IDs, counts,
 * bands, costs, timestamps — never raw emails, document text, prompts, or model output.
 */

import type { Database } from '@/lib/supabase/types';
import type { LlmUsageRow } from './use-llm-usage';

// ============================================================================
// Section 1 — AI Health Today (over llm_usage_daily rows)
// ============================================================================

export interface HealthDay {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  errors: number;
  errorRate: number; // 0..1
  avgLatencyMs: number;
}

export interface HealthModelRow {
  provider: string;
  model: string;
  calls: number;
  costUsd: number;
  errors: number;
}

export interface HealthTrendPoint {
  day: string;
  costUsd: number;
  calls: number;
}

export interface HealthToday {
  today: HealthDay;
  yesterday: HealthDay;
  /** Provider/model breakdown for today (falls back to yesterday when today is empty). */
  breakdown: HealthModelRow[];
  breakdownDay: string;
  /** Per-day cost + calls across the whole window, ascending — feeds the trend chart. */
  trend: HealthTrendPoint[];
  hasAnyData: boolean;
}

const EMPTY_DAY: HealthDay = {
  calls: 0,
  inputTokens: 0,
  outputTokens: 0,
  costUsd: 0,
  errors: 0,
  errorRate: 0,
  avgLatencyMs: 0,
};

/** UTC date string (YYYY-MM-DD) `offset` days before `nowMs`. */
export function utcDay(nowMs: number, offset = 0): string {
  return new Date(nowMs - offset * 86_400_000).toISOString().slice(0, 10);
}

function aggregateDay(rows: LlmUsageRow[]): HealthDay {
  let calls = 0;
  let inputTokens = 0;
  let outputTokens = 0;
  let costUsd = 0;
  let errors = 0;
  let latency = 0;
  for (const r of rows) {
    calls += r.calls || 0;
    inputTokens += Number(r.input_tokens) || 0;
    outputTokens += Number(r.output_tokens) || 0;
    costUsd += Number(r.cost_usd) || 0;
    errors += r.errors || 0;
    latency += Number(r.total_latency_ms) || 0;
  }
  return {
    calls,
    inputTokens,
    outputTokens,
    costUsd,
    errors,
    errorRate: calls > 0 ? errors / calls : 0,
    avgLatencyMs: calls > 0 ? latency / calls : 0,
  };
}

function breakdownForDay(rows: LlmUsageRow[]): HealthModelRow[] {
  const map = new Map<string, HealthModelRow>();
  for (const r of rows) {
    const key = `${r.provider}/${r.model}`;
    const m = map.get(key) ?? { provider: r.provider, model: r.model, calls: 0, costUsd: 0, errors: 0 };
    m.calls += r.calls || 0;
    m.costUsd += Number(r.cost_usd) || 0;
    m.errors += r.errors || 0;
    map.set(key, m);
  }
  return [...map.values()].sort((a, b) => b.costUsd - a.costUsd);
}

/**
 * Split the daily rollup into today vs yesterday plus a provider/model breakdown.
 * Pure — the reference "now" is injected so it is deterministic in tests.
 */
export function summarizeHealthToday(rows: LlmUsageRow[], nowMs: number): HealthToday {
  const todayStr = utcDay(nowMs, 0);
  const yesterdayStr = utcDay(nowMs, 1);
  const todayRows = rows.filter((r) => r.day === todayStr);
  const yesterdayRows = rows.filter((r) => r.day === yesterdayStr);

  // Prefer today's breakdown; fall back to yesterday's when today hasn't recorded yet.
  const breakdownRows = todayRows.length > 0 ? todayRows : yesterdayRows;
  const breakdownDay = todayRows.length > 0 ? todayStr : yesterdayStr;

  // Per-day cost + calls across the window (ascending) for the trend chart.
  const dayMap = new Map<string, HealthTrendPoint>();
  for (const r of rows) {
    const d = dayMap.get(r.day) ?? { day: r.day, costUsd: 0, calls: 0 };
    d.costUsd += Number(r.cost_usd) || 0;
    d.calls += r.calls || 0;
    dayMap.set(r.day, d);
  }
  const trend = [...dayMap.values()].sort((a, b) => a.day.localeCompare(b.day));

  return {
    today: todayRows.length > 0 ? aggregateDay(todayRows) : EMPTY_DAY,
    yesterday: yesterdayRows.length > 0 ? aggregateDay(yesterdayRows) : EMPTY_DAY,
    breakdown: breakdownForDay(breakdownRows),
    breakdownDay,
    trend,
    hasAnyData: rows.length > 0,
  };
}

// ============================================================================
// Section 5 — Recomputed usage alerts (over llm_usage_daily rows)
// ============================================================================
// Mirrors the pure decision logic in
// supabase/functions/llm-usage-alert/helpers.ts (computeAlerts + DEFAULT_THRESHOLDS).
// The scheduled GitHub Actions cron + Supabase logs remain the AUTHORITATIVE alert
// channel (there is no llm_usage_alerts table to read); this is an in-app preview.

export type AlertKind = 'cost_spike' | 'error_rate' | 'latency';
export interface UsageAlert {
  kind: AlertKind;
  message: string;
}

export const ALERT_THRESHOLDS = {
  costMult: 3,
  costFloorUsd: 0.5,
  errorRate: 0.1,
  latencyMult: 2,
  minCalls: 20,
  minTrailingDays: 3,
} as const;

export interface RecomputedAlerts {
  alerts: UsageAlert[];
  yesterdayHasData: boolean;
  trailingDays: number;
}

/** Recompute the daily usage alert signal from the rollup rows. Pure; `nowMs` injected. */
export function recomputeAlerts(rows: LlmUsageRow[], nowMs: number): RecomputedAlerts {
  const t = ALERT_THRESHOLDS;
  const yesterdayStr = utcDay(nowMs, 1);
  // Trailing window = the 7 days before yesterday (utcDay 8..2), matching the cron.
  const trailingStrs: string[] = [];
  for (let o = 2; o <= 8; o++) trailingStrs.push(utcDay(nowMs, o));

  const yesterday = aggregateDay(rows.filter((r) => r.day === yesterdayStr));
  const trailing = trailingStrs
    .map((d) => aggregateDay(rows.filter((r) => r.day === d)))
    .filter((agg) => agg.calls > 0);

  const alerts: UsageAlert[] = [];
  const yesterdayHasData = yesterday.calls > 0;
  if (!yesterdayHasData) return { alerts, yesterdayHasData, trailingDays: trailing.length };

  const enoughHistory = trailing.length >= t.minTrailingDays;

  if (enoughHistory) {
    const avgCost = trailing.reduce((s, d) => s + d.costUsd, 0) / trailing.length;
    if (yesterday.costUsd >= t.costFloorUsd && avgCost > 0 && yesterday.costUsd > t.costMult * avgCost) {
      alerts.push({
        kind: 'cost_spike',
        message: `Cost spike: $${yesterday.costUsd.toFixed(2)} yesterday vs $${avgCost.toFixed(2)} trailing ${trailing.length}-day avg (>${t.costMult}×).`,
      });
    }
  }

  if (yesterday.calls >= t.minCalls) {
    const rate = yesterday.errors / yesterday.calls;
    if (rate > t.errorRate) {
      alerts.push({
        kind: 'error_rate',
        message: `Error rate ${(rate * 100).toFixed(1)}% (${yesterday.errors}/${yesterday.calls}) vs ${(t.errorRate * 100).toFixed(0)}% threshold.`,
      });
    }
  }

  if (enoughHistory && yesterday.calls >= t.minCalls) {
    const trailingCalls = trailing.reduce((s, d) => s + d.calls, 0);
    const trailingLatency = trailing.reduce((s, d) => s + d.avgLatencyMs * d.calls, 0);
    const avgTrailingMs = trailingCalls > 0 ? trailingLatency / trailingCalls : 0;
    if (avgTrailingMs > 0 && yesterday.avgLatencyMs > t.latencyMult * avgTrailingMs) {
      alerts.push({
        kind: 'latency',
        message: `Latency ${Math.round(yesterday.avgLatencyMs)}ms avg/call yesterday vs ${Math.round(avgTrailingMs)}ms trailing avg (>${t.latencyMult}×).`,
      });
    }
  }

  return { alerts, yesterdayHasData, trailingDays: trailing.length };
}

// ============================================================================
// Section 2 — Latest scoring eval run (scoring_evals)
// ============================================================================

export type ScoringEvalRow = Database['public']['Tables']['scoring_evals']['Row'];

export interface ScoringEvalCase {
  caseName: string;
  expectedBand: string;
  actualBand: string;
  finalScore: number;
  inBand: boolean;
  spread: number;
}

export interface LatestScoringRun {
  runId: string;
  methodologyVersion: string;
  createdAt: string;
  passCount: number;
  failCount: number;
  total: number;
  cases: ScoringEvalCase[];
}

/**
 * Reduce recent scoring_evals rows (created_at DESC) to the single latest run.
 * A "run" is all rows sharing the most-recent row's run_id. Returns null when the
 * table is empty (no live scoring eval has persisted yet).
 */
export function summarizeLatestRun(rows: ScoringEvalRow[]): LatestScoringRun | null {
  if (rows.length === 0) return null;
  const latestRunId = rows[0].run_id;
  const runRows = rows.filter((r) => r.run_id === latestRunId);
  const cases: ScoringEvalCase[] = runRows.map((r) => ({
    caseName: r.case_name,
    expectedBand: r.expected_band,
    actualBand: r.actual_band,
    finalScore: r.final_score,
    inBand: r.in_band,
    spread: r.spread,
  }));
  const passCount = cases.filter((c) => c.inBand).length;
  return {
    runId: latestRunId,
    methodologyVersion: runRows[0].methodology_version,
    createdAt: runRows[0].created_at,
    passCount,
    failCount: cases.length - passCount,
    total: cases.length,
    cases: cases.sort((a, b) => a.caseName.localeCompare(b.caseName)),
  };
}

// ============================================================================
// Section 3 — DD retrieval evidence (analytics_events, dd_rag_retrieval)
// ============================================================================

/** Below this raw top-similarity a retrieval is flagged (see rag/retrieval eval fixtures). */
export const RETRIEVAL_LOW_SIMILARITY = 0.5;

export type RetrievalStatus = 'healthy' | 'warning-empty' | 'warning-low';

export interface RetrievalEvent {
  startupId: string | null;
  chunkCount: number;
  topSimilarity: number;
  createdAt: string;
  status: RetrievalStatus;
}

/** Green when chunks came back with a healthy top hit; warn on empty or weak retrieval. */
export function labelRetrieval(chunkCount: number, topSimilarity: number): RetrievalStatus {
  if (chunkCount <= 0) return 'warning-empty';
  if (topSimilarity < RETRIEVAL_LOW_SIMILARITY) return 'warning-low';
  return 'healthy';
}

interface RagRetrievalProps {
  startupId?: string;
  chunkCount?: number;
  topSimilarity?: number;
}

/** Map raw analytics_events rows into labeled retrieval evidence. Pure. */
export function mapRetrievalEvents(rows: { properties: unknown; created_at: string }[]): RetrievalEvent[] {
  return rows.map((row) => {
    const p = (row.properties ?? {}) as RagRetrievalProps;
    const chunkCount = typeof p.chunkCount === 'number' ? p.chunkCount : 0;
    const topSimilarity = typeof p.topSimilarity === 'number' ? p.topSimilarity : 0;
    return {
      startupId: typeof p.startupId === 'string' ? p.startupId : null,
      chunkCount,
      topSimilarity,
      createdAt: row.created_at,
      status: labelRetrieval(chunkCount, topSimilarity),
    };
  });
}

export interface RagRetrievalResult {
  events: RetrievalEvent[];
  /**
   * analytics_events is gated to super_admin/analyst; a plain admin's RLS filter
   * returns zero rows silently. We can't distinguish "no events" from "no access",
   * so the UI states both honestly when empty.
   */
  empty: boolean;
}

// ============================================================================
// Proof / DD-flow evidence (ai_observability_events) — the real, queryable table
// ============================================================================

export interface ObservabilityEventRow {
  event_name: string;
  startup_id: string | null;
  document_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface ProofEventStat {
  count: number;
  lastSeen: string | null; // ISO timestamp of the most recent occurrence
}

/** event_name → { count, lastSeen } over the observability rows. Pure. */
export function summarizeProofEvents(rows: ObservabilityEventRow[]): Record<string, ProofEventStat> {
  const out: Record<string, ProofEventStat> = {};
  for (const r of rows) {
    const s = out[r.event_name] ?? { count: 0, lastSeen: null };
    s.count += 1;
    if (!s.lastSeen || r.created_at > s.lastSeen) s.lastSeen = r.created_at;
    out[r.event_name] = s;
  }
  return out;
}

export interface FlowEvent {
  eventName: string;
  /** A safe correlation id (startup / document / submission) — never PII. */
  ref: string | null;
  createdAt: string;
}

// ============================================================================
// Eval Quality History (ai_eval_runs) — RAG faithfulness / retrieval / extraction
// ============================================================================

export type EvalSuite = 'rag_faithfulness' | 'rag_retrieval' | 'extraction';

export interface EvalRunRow {
  run_id: string;
  suite: string;
  total: number;
  passed: number;
  failed: number;
  env: string | null;
  created_at: string;
}

/** Human labels for the eval suites shown in the dashboard. */
export const EVAL_SUITE_LABEL: Record<string, string> = {
  rag_faithfulness: 'RAG faithfulness',
  rag_retrieval: 'RAG retrieval',
  extraction: 'Extraction',
  scoring: 'Scoring',
};

/** The most recent run per suite (rows arrive created_at DESC). Pure. */
export function latestRunPerSuite(rows: EvalRunRow[]): EvalRunRow[] {
  const seen = new Set<string>();
  const out: EvalRunRow[] = [];
  for (const r of rows) {
    if (seen.has(r.suite)) continue;
    seen.add(r.suite);
    out.push(r);
  }
  return out;
}

/** Two sample runs per RAG/extraction suite for mock/demo mode. */
export function sampleEvalRuns(nowMs: number): EvalRunRow[] {
  const specs: { suite: EvalSuite; total: number; passed: number; hoursAgo: number }[] = [
    { suite: 'rag_faithfulness', total: 2, passed: 2, hoursAgo: 3 },
    { suite: 'rag_retrieval', total: 2, passed: 2, hoursAgo: 4 },
    { suite: 'extraction', total: 3, passed: 3, hoursAgo: 5 },
    { suite: 'rag_faithfulness', total: 2, passed: 1, hoursAgo: 27 },
    { suite: 'extraction', total: 3, passed: 2, hoursAgo: 29 },
  ];
  return specs.map((s, i) => ({
    run_id: `sample-eval-${s.suite}-${i}`,
    suite: s.suite,
    total: s.total,
    passed: s.passed,
    failed: s.total - s.passed,
    env: 'staging',
    created_at: new Date(nowMs - s.hoursAgo * 3_600_000).toISOString(),
  }));
}

/** Most-recent observability events as a safe feed (ids/timestamps only). Pure. */
export function recentFlowEvents(rows: ObservabilityEventRow[], limit = 15): FlowEvent[] {
  return [...rows]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, limit)
    .map((r) => {
      const submissionId =
        r.metadata && typeof r.metadata.submissionId === 'string' ? r.metadata.submissionId : null;
      return {
        eventName: r.event_name,
        ref: r.startup_id ?? r.document_id ?? submissionId,
        createdAt: r.created_at,
      };
    });
}

// ============================================================================
// Sample data (mock / demo mode ONLY)
// ============================================================================
// Populates the figures in mock/demo mode so the cockpit looks alive in a demo,
// matching the sibling admin dashboards (Analytics, Product) which also render
// sample data under `mode.showSampleData`. NEVER used in production — the hooks
// gate these behind `mode.showSampleData`, and every id is prefixed `sample-`/
// `demo-` so it can't be mistaken for real data. Deterministic (no randomness).

/** 30 days × 2 models of realistic LLM usage, waved by day. `nowMs` anchors "today". */
export function sampleLlmUsageRows(nowMs: number): LlmUsageRow[] {
  // Per-call cost is scaled to a few dollars/day so the demo trend chart's axis reads
  // cleanly (sub-dollar days round every y-tick to 0/1). Sample data only.
  const models = [
    { provider: 'mistral', model: 'mistral-large-latest', baseCalls: 62, costPer: 0.12, inTok: 1850, outTok: 720, latency: 940 },
    { provider: 'mistral', model: 'mistral-small-latest', baseCalls: 44, costPer: 0.026, inTok: 1200, outTok: 380, latency: 520 },
  ];
  const rows: LlmUsageRow[] = [];
  for (let o = 29; o >= 0; o--) {
    const day = utcDay(nowMs, o);
    const wave = 0.72 + 0.28 * Math.sin(o / 3.1); // deterministic weekday-ish swell
    for (const m of models) {
      const calls = Math.max(1, Math.round(m.baseCalls * wave));
      const errors = o % 9 === 0 ? Math.round(calls * 0.06) : Math.round(calls * 0.008);
      rows.push({
        day,
        provider: m.provider,
        model: m.model,
        calls,
        errors,
        input_tokens: calls * m.inTok,
        output_tokens: calls * m.outTok,
        cost_usd: Number((calls * m.costPer).toFixed(4)),
        total_latency_ms: calls * m.latency,
        updated_at: new Date(nowMs).toISOString(),
      } as LlmUsageRow);
    }
  }
  return rows;
}

/** A representative latest scoring run: five band-spanning cases, one just out of band. */
export function sampleScoringRun(nowMs: number): LatestScoringRun {
  const cases: ScoringEvalCase[] = [
    { caseName: '01-critical-uniform-2', expectedBand: 'critical', actualBand: 'critical', finalScore: 18, inBand: true, spread: 2 },
    { caseName: '02-weak-uniform-4', expectedBand: 'weak', actualBand: 'weak', finalScore: 39, inBand: true, spread: 3 },
    { caseName: '03-moderate-uniform-6', expectedBand: 'moderate', actualBand: 'moderate', finalScore: 57, inBand: true, spread: 4 },
    { caseName: '04-strong-uniform-8', expectedBand: 'strong', actualBand: 'strong', finalScore: 78, inBand: true, spread: 2 },
    { caseName: '05-excellent-uniform-9', expectedBand: 'excellent', actualBand: 'strong', finalScore: 86, inBand: false, spread: 5 },
  ];
  const passCount = cases.filter((c) => c.inBand).length;
  return {
    runId: 'sample-run-v2_1',
    methodologyVersion: 'v2.1',
    createdAt: new Date(nowMs - 2 * 3_600_000).toISOString(),
    passCount,
    failCount: cases.length - passCount,
    total: cases.length,
    cases,
  };
}

/** Eight recent retrieval events spanning healthy / weak / empty outcomes. */
export function sampleRetrievalEvents(nowMs: number): RetrievalEvent[] {
  const raw = [
    { chunkCount: 6, topSimilarity: 0.82 },
    { chunkCount: 8, topSimilarity: 0.88 },
    { chunkCount: 4, topSimilarity: 0.74 },
    { chunkCount: 5, topSimilarity: 0.69 },
    { chunkCount: 7, topSimilarity: 0.79 },
    { chunkCount: 3, topSimilarity: 0.44 },
    { chunkCount: 0, topSimilarity: 0 },
    { chunkCount: 2, topSimilarity: 0.58 },
  ];
  return raw.map((r, i) => ({
    startupId: `demo-startup-${(i % 3) + 1}`,
    chunkCount: r.chunkCount,
    topSimilarity: r.topSimilarity,
    createdAt: new Date(nowMs - (i + 1) * 37 * 60_000).toISOString(),
    status: labelRetrieval(r.chunkCount, r.topSimilarity),
  }));
}

/** ~49 recent proof/observability events across the seven persisted event types. */
export function sampleObservabilityEvents(nowMs: number): ObservabilityEventRow[] {
  const names = [
    'processing_complete',
    'embedding_reuse_summary',
    'score_persisted',
    'dd_rag_request',
    'submission_info_requested',
    'submission_claim_invited',
    'submission_claimed',
  ];
  const rows: ObservabilityEventRow[] = [];
  for (let i = 0; i < 49; i++) {
    const name = names[i % names.length];
    const isSubmission = name.startsWith('submission');
    const isDoc = name === 'processing_complete' || name === 'embedding_reuse_summary';
    rows.push({
      event_name: name,
      startup_id: isSubmission ? null : `demo-startup-${(i % 3) + 1}`,
      document_id: isDoc ? `demo-doc-${(i % 5) + 1}` : null,
      metadata: isSubmission ? { submissionId: `demo-sub-${(i % 4) + 1}` } : {},
      created_at: new Date(nowMs - i * 47 * 60_000).toISOString(),
    });
  }
  return rows;
}
